import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import './Hyperspeed.css';

const Hyperspeed = () => {
  const containerRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Create scene
    const scene = new THREE.Scene();

    // Create camera
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.z = 5;

    // Create renderer
    const renderer = new THREE.WebGLRenderer({ 
      alpha: true, 
      antialias: true 
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x000000, 0);

    // Add renderer to container
    containerRef.current.appendChild(renderer.domElement);

    // Create road geometry
    const roadGeometry = new THREE.PlaneGeometry(20, 200);
    const roadMaterial = new THREE.MeshBasicMaterial({ 
      color: 0x0a0a0a,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.8
    });
    const road = new THREE.Mesh(roadGeometry, roadMaterial);
    road.rotation.x = -Math.PI / 2;
    road.position.y = -2;
    scene.add(road);

    // Create road lines
    const lineGeometry = new THREE.PlaneGeometry(0.1, 200);
    const lineMaterial = new THREE.MeshBasicMaterial({ 
      color: 0x00ffff,
      transparent: true,
      opacity: 0.6
    });

    // Center line
    const centerLine = new THREE.Mesh(lineGeometry, lineMaterial);
    centerLine.rotation.x = -Math.PI / 2;
    centerLine.position.y = -1.9;
    scene.add(centerLine);

    // Side lines
    const leftLine = new THREE.Mesh(lineGeometry, lineMaterial);
    leftLine.rotation.x = -Math.PI / 2;
    leftLine.position.y = -1.9;
    leftLine.position.x = -5;
    scene.add(leftLine);

    const rightLine = new THREE.Mesh(lineGeometry, lineMaterial);
    rightLine.rotation.x = -Math.PI / 2;
    rightLine.position.y = -1.9;
    rightLine.position.x = 5;
    scene.add(rightLine);

    // Create moving lights
    const lightGeometry = new THREE.SphereGeometry(0.05, 8, 8);
    const lightMaterial = new THREE.MeshBasicMaterial({ 
      color: 0x00ffff,
      transparent: true,
      opacity: 0.8
    });

    const lights = [];
    for (let i = 0; i < 30; i++) {
      const light = new THREE.Mesh(lightGeometry, lightMaterial);
      light.position.x = (Math.random() - 0.5) * 8;
      light.position.y = -1.8;
      light.position.z = -Math.random() * 200;
      scene.add(light);
      lights.push(light);
    }

    // Create background particles
    const particleGeometry = new THREE.BufferGeometry();
    const particleCount = 500;
    const positions = new Float32Array(particleCount * 3);

    for (let i = 0; i < particleCount * 3; i++) {
      positions[i] = (Math.random() - 0.5) * 200;
    }

    particleGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const particleMaterial = new THREE.PointsMaterial({
      color: 0x333333,
      size: 0.05,
      transparent: true,
      opacity: 0.4
    });

    const particles = new THREE.Points(particleGeometry, particleMaterial);
    scene.add(particles);

    // Animation loop
    const animate = () => {
      animationRef.current = requestAnimationFrame(animate);

      const time = Date.now() * 0.001;

      // Move lights
      lights.forEach((light, index) => {
        light.position.z += 0.8;
        if (light.position.z > 10) {
          light.position.z = -200;
          light.position.x = (Math.random() - 0.5) * 8;
        }
        
        // Pulsing effect
        light.scale.setScalar(1 + Math.sin(time * 3 + index) * 0.2);
      });

      // Rotate particles
      particles.rotation.y = time * 0.05;

      // Move road
      road.position.z = (time * 15) % 200 - 100;

      renderer.render(scene, camera);
    };

    animate();

    // Handle resize
    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      window.removeEventListener('resize', handleResize);
      if (containerRef.current && renderer.domElement) {
        containerRef.current.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, []);

  return <div ref={containerRef} className="hyperspeed-container" />;
};

export default Hyperspeed;
